'use client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed } from '@/lib/store';
import { peut, estBiologiste, fmtDate, aujourdhuiIso } from '@/lib/meta';
import { I, card, inputStyle, FieldError, useBusy, RefISO, Selecteur, useConfirmation } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { useAutorisationResponsable, ChampsResponsable } from '@/components/AutorisationResponsable';
import { Tableau } from '@/shared/Tableau';
import {
  ActionsLigne, BadgeArchive, FormulaireLigne,
} from '@/components/RegistreEdition';
import type { ChampDef } from '@/components/RegistreEdition';
import { H1 } from './commun';

/* ─────────── EEQ — envois (lot C5, renommé le 27/08/2026) ───────────
 * Ce que le laboratoire TRANSMET à l'organisme, par opposition à l'écran
 * « Évaluation externe » qui montre ce qu'il REÇOIT (z-scores, actions).
 *
 * ⚠️ S'appelait « CQE » jusqu'au 27/08/2026. Arbitrage d'Inès : un seul sigle,
 * EEQ, pour les deux faces de la même chose. Le mot « CQE » est réservé à
 * l'usage historique du laboratoire et reste dans les synonymes de recherche.
 *
 * ⚠️ CE N'EST PAS UN EIL. Inès veut par ailleurs organiser des échanges
 * inter-laboratoires locaux, sur les paramètres sans abonnement EEQ. Cet écran
 * ne sait pas le faire, et le renommer ne l'y aiderait pas : un EIL n'a AUCUNE
 * valeur assignée par un organisme — la référence est la MOYENNE DES
 * PARTICIPANTS, et il n'existe ici aucun endroit pour porter les valeurs des
 * confrères ni pour compter combien ils sont. Un mot juste sur un mécanisme
 * faux vaut moins qu'un mot imparfait. L'EIL est un chantier à part.
 *
 * L'écran existe pour deux raisons que le tableur ne couvrait pas :
 *
 *  · La DATE LIMITE figurait sur les feuilles sans qu'aucune alerte n'en
 *    découle — d'où le « mois raté » de mai 2026 en bactériologie. Ici un envoi
 *    dépassé se signale de lui-même.
 *
 *  · La règle « on ne transmet qu'avec un CQI conforme » était déclarative :
 *    une colonne « CQI CONFORME ? » qu'on pouvait laisser à NON en transmettant
 *    quand même. Ici l'état du CQI est LU EN BASE et une transmission malgré un
 *    rejet exige une dérogation nominative du biologiste.
 * ------------------------------------------------------------------------- */

type Envoi = {
  envoiId: string; ref: string | null; numeroEchantillon: string | null;
  periodeLibelle: string | null; dateLimite: string | null; datePassage: string | null;
  dateEnvoi: string | null; statut: 'planifie' | 'passe' | 'envoye' | 'rate';
  checkListCoches: number; checkListTotal: number;
  mesures: number; derogations: number; enRetard: boolean; importe: boolean;
  cqiRejet: number; cqiInconnu: number; declarationContredite: number;
  passeEnRetard: boolean;
};
type Programme = {
  programmeId: string; code: string; libelle: string; organisme: string;
  discipline: string; cycle: string | null; reference: string | null;
  periodicite: string; checkList: string[]; envois: Envoi[];
  // Édition du programme (lot 2, 10/08/2026).
  numeroLaboratoire: string; lotControle: string; datePeremption: string;
  archive: boolean; archiveMotif: string;
};

/* Champs éditables d'un programme — le catalogue n'est plus figé (lot 2).
 * `checkList` reste hors périmètre : c'est un gabarit structuré, pas un champ. */
const PROGRAMME_CHAMPS: readonly ChampDef[] = [
  { k: 'code', label: 'Code', requis: true, fige: true },
  { k: 'libelle', label: 'Libellé', requis: true },
  { k: 'organisme', label: 'Organisme' },
  { k: 'discipline', label: 'Discipline' },
  { k: 'reference', label: 'Référence' },
  { k: 'cycle', label: 'Cycle en cours' },
  { k: 'numeroLaboratoire', label: 'N° laboratoire' },
  { k: 'periodicite', label: 'Périodicité' },
  { k: 'lotControle', label: 'Lot de contrôle' },
  { k: 'datePeremption', label: 'Péremption du lot' },
];
type Mesure = {
  mesureId: string; analyte: string | null; analyteLibelle: string;
  equipementCode: string | null; valeur: number | null; unite: string | null;
  cqiConforme: boolean | null; cqiStatut: string | null; dateCqi: string | null;
  cqiPassage: string | null; cqiDeclareFeuille: string | null; importe: boolean;
  derogationPar: string | null; derogationMotif: string | null;
  /** C6 — le z-score revenu de l'organisme, quand il a pu être rapproché.
   *  `null` veut dire « pas de retour RAPPROCHÉ », jamais « pas de retour reçu ». */
  retour: {
    eeqRef: string; zscore: number | null; resultat: string;
    dateEeq: string; precisionDate: string; programme: string; actionRef: string;
  } | null;
};
type Proposable = {
  analyte: string; analyteNom: string; equipement: string; equipementNom: string;
  discipline: string; unite: string | null;
};
type Detail = {
  /** Bilan du rapprochement envoi ↔ retour, pour cet envoi. */
  rapprochement?: { apparies: number; mesuresSansRetour: number };
  envoi: { envoiId: string; numeroEchantillon: string | null; periodeLibelle: string | null;
    dateLimite: string | null; datePassage: string | null; statut: string;
    checkList: boolean[]; commentaire: string | null };
  programme: { code: string; libelle: string; cycle: string | null; checkList: string[]; discipline: string } | null;
  mesures: Mesure[];
  proposables: Proposable[];
};
type EtatCqi = {
  conforme: boolean | null; statut?: string; datePassage?: string;
  joursEcart?: number; motif?: string; equipement?: string | null;
};

const STATUTS: Record<string, { label: string; bg: string; fg: string }> = {
  planifie: { label: 'Planifié', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  passe: { label: 'Passé', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  envoye: { label: 'Transmis', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  rate: { label: 'Raté', bg: 'var(--red-50)', fg: 'var(--red-600)' },
};

/** Un CQI vieux de plusieurs jours n'atteste pas des conditions du jour du
 *  passage. Au-delà de ce délai on le signale, sans l'interdire. */
const ECART_CQI_ALERTE = 7;
const MOTIF_MIN = 20;

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4,
};
const aujourdhui = () => aujourdhuiIso();

/* ─────────── Bilan GLOBAL du rapprochement envoi ↔ retour (GED-15 c) ─────────
 *
 * `GET /ged/cqe/rapprochement` était autorisée depuis sa livraison sans qu'aucun
 * écran ne l'atteigne — alors que 44 assertions la testent déjà. Ce qu'elle sert
 * n'existe nulle part ailleurs : le panneau d'un envoi ne connaît QUE son envoi,
 * donc il dit « 3 mesures sans retour » sans jamais pouvoir dire l'inverse —
 * combien de z-scores reçus ne se rattachent à AUCUN envoi enregistré.
 *
 * Or c'est ce sens-là qui compte en accréditation. Une mesure sans retour peut
 * n'être qu'un délai ; un z-score orphelin, lui, veut dire que le laboratoire a
 * participé à une épreuve dont l'envoi n'est pas tracé — ou que le registre des
 * EEQ porte une date au mois près, ou pas de code analyte. Les trois se
 * distinguent, et le bilan les compte séparément plutôt que de les fondre.
 *
 * ⚠️ Chargé AU CLIC, pas au montage : il relit les 435 mesures et tous les EEQ,
 * page par page (`maxLimit: 100`). Cet écran s'ouvre neuf fois sur dix pour
 * saisir un envoi, pas pour faire un bilan.
 *
 * ⚠️ Il n'écrit RIEN et ne rattache rien. Le rapprochement est reconstruit à
 * chaque lecture sur (discipline | mois | analyte | automate) : les deux
 * registres n'ont aucune clé commune, et fabriquer cette clé en base figerait
 * une correspondance devinée en fait acquis.
 */
type Bilan = {
  bilan: {
    mesures: number; eeq: number; apparies: number; mesuresSansRetour: number;
    eeqSansEnvoi: number; eeqSansDate: number; eeqSansCodeAnalyte: number; ambigus: number;
  };
  ambiguites: string[];
  couples: {
    mesureId: string; envoiRef: string; periodeLibelle: string;
    analyte: string; analyteLibelle: string; equipementCode: string | null;
    valeur: number | null; unite: string | null;
    zscore: number | null; resultat: string; eeqRef: string;
    dateEeq: string; precisionDate: string; actionRef: string;
  }[];
};

function Compteur({ n, quoi, ton, aide }: {
  n: number; quoi: string; ton?: 'alerte' | 'bon'; aide: string;
}) {
  const fg = ton === 'alerte' ? 'var(--red-600)' : ton === 'bon' ? 'var(--green-600)' : 'var(--text-strong)';
  return (
    <div title={aide} style={{ cursor: 'help', minWidth: 128 }}>
      <div style={{ fontSize: 21, fontWeight: 700, color: fg, lineHeight: 1.1 }}>{n}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.4, marginTop: 2 }}>{quoi}</div>
    </div>
  );
}

function BilanRapprochement() {
  const [ouvert, setOuvert] = useState(false);
  const [etat, setEtat] = useState<Bilan | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const { run, estOccupe } = useBusy();

  const charger = async () => {
    setErreur(null);
    const r = await fetch('/api/ged/ged/cqe/rapprochement', { credentials: 'include', cache: 'no-store' });
    // Un échec est DIT. Un bilan vide se lirait « tout est rapproché » — le
    // message qu'un écran d'accréditation ne doit jamais fabriquer à partir
    // d'un 403.
    if (!r.ok) { setErreur(`Le bilan n’a pas pu être établi (${r.status}).`); return; }
    setEtat(await r.json());
  };

  const basculer = () => {
    const versOuvert = !ouvert;
    setOuvert(versOuvert);
    if (versOuvert && !etat) void run('bilan', charger);
  };

  const b = etat?.bilan;
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <button onClick={basculer} aria-expanded={ouvert} className="ged-btn"
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <I n={ouvert ? 'chevron-bas' : 'chevron-right'} s={14} />
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          Bilan du rapprochement envois ↔ retours
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          ce que nous avons transmis, en regard de ce que les organismes ont renvoyé
        </span>
        <span style={{ flex: 1 }} />
        {estOccupe('bilan') && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>lecture…</span>}
      </button>

      {ouvert && (
        <div style={{ padding: '0 14px 16px', borderTop: '1px solid var(--border-default)' }}>
          {erreur && (
            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--red-600)', fontWeight: 700 }}>
              {erreur} Ce n’est pas « rien à rapprocher » : c’est une lecture qui a échoué.
            </p>
          )}
          {!erreur && !b && (
            <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Lecture des mesures transmises et des retours enregistrés…
            </p>
          )}
          {b && (
            <>
              <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', margin: '14px 0 0' }}>
                <Compteur n={b.mesures} quoi="mesures transmises"
                  aide="Toutes les mesures enregistrées sur les envois du laboratoire." />
                <Compteur n={b.eeq} quoi="retours enregistrés"
                  aide="Toutes les lignes du registre des évaluations externes reçues." />
                <Compteur n={b.apparies} quoi="couples rapprochés" ton="bon"
                  aide="Une mesure transmise et un z-score reçu qui se correspondent sur la discipline, le mois, le paramètre et l’automate." />
                <Compteur n={b.mesuresSansRetour} quoi="mesures sans retour rapproché"
                  aide="Pas forcément sans réponse de l’organisme : le retour peut être arrivé sans qu’on puisse le rattacher." />
                <Compteur n={b.eeqSansEnvoi} quoi="retours sans envoi tracé" ton={b.eeqSansEnvoi ? 'alerte' : undefined}
                  aide="Un z-score reçu pour une épreuve dont aucun envoi n’est enregistré ici. C’est le sens qui compte en audit : la participation a eu lieu, sa trace manque." />
                <Compteur n={b.ambigus} quoi="cas ambigus" ton={b.ambigus ? 'alerte' : undefined}
                  aide="Plusieurs mesures ou plusieurs retours se disputent la même clé : rien n’est apparié d’office." />
              </div>

              <p style={{ margin: '14px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '86ch' }}>
                Les deux registres n’ont <strong>aucune clé commune</strong> : le rapprochement est
                reconstruit à chaque lecture sur la discipline, le mois, l’analyte et l’automate, et
                n’est <strong>jamais écrit en base</strong> — une correspondance devinée ne doit pas
                devenir un fait acquis. Deux causes de non-rapprochement sont comptées à part parce
                qu’elles se corrigent au registre des EEQ, pas ici :{' '}
                <strong>{b.eeqSansDate}</strong> retour(s) sans date exploitable et{' '}
                <strong>{b.eeqSansCodeAnalyte}</strong> sans code de paramètre.
              </p>

              {etat.ambiguites.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ ...lbl, marginBottom: 6 }}>Cas ambigus — montrés, pas tus</div>
                  <ul style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 4 }}>
                    {etat.ambiguites.map((a) => (
                      <li key={a} style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>{a}</li>
                    ))}
                  </ul>
                </div>
              )}

              {etat.couples.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ ...lbl, marginBottom: 6 }}>
                    Les {etat.couples.length} couples rapprochés, du plus récent au plus ancien
                  </div>
                  <Tableau
                    defilementHorizontal
                    id="cqe-couples"
                    ajusterLargeur
                    /* L6 (10/10/2026) : niveau STANDARD — paramètre, automate, envoi en
                       liste ; transmis et z en nombres (É-4) ; reçu le en dates. */
                    niveau="standard"
                    lignes={etat.couples}
                    cleLigne={(c) => String(c.mesureId)}
                    colonnes={[
                      {
                        cle: 'envoi', libelle: 'Envoi', type: 'liste',
                        valeur: (c) => c.periodeLibelle || c.envoiRef || '',
                        style: { color: 'var(--text-muted)' },
                        rendu: (c) => c.periodeLibelle || c.envoiRef || '—',
                      },
                      {
                        cle: 'analyte', libelle: 'Paramètre', type: 'liste', epinglee: true, obligatoire: true,
                        valeur: (c) => c.analyteLibelle || c.analyte || '',
                        style: { color: 'var(--text-strong)' },
                        rendu: (c) => c.analyteLibelle || c.analyte,
                      },
                      {
                        cle: 'equipement', libelle: 'Automate', type: 'liste', valeur: (c) => c.equipementCode || '',
                        style: { fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' },
                        rendu: (c) => c.equipementCode || '—',
                      },
                      {
                        cle: 'valeur', libelle: 'Transmis', type: 'nombre', valeur: (c) => c.valeur,
                        rendu: (c) => (c.valeur === null ? '—' : `${c.valeur}${c.unite ? ` ${c.unite}` : ''}`),
                      },
                      {
                        cle: 'zscore', libelle: 'z reçu', type: 'nombre',
                        /* Trié sur la VALEUR ABSOLUE : ce qu'on cherche en
                           triant un z, ce sont les écarts les plus grands,
                           qu'ils soient en excès ou en défaut. Un tri signé
                           mettrait −3 et +3 aux deux bouts de la liste alors
                           qu'ils posent le même problème. */
                        valeur: (c) => (c.zscore === null ? null : Math.abs(Number(c.zscore))),
                        rendu: (c) => (
                          <span style={{ fontWeight: 700, color: c.resultat === 'satisfaisant' ? 'var(--green-600)' : 'var(--red-600)' }}>
                            {c.zscore === null ? '—' : c.zscore}
                            {c.actionRef ? ` · ${c.actionRef}` : ''}
                          </span>
                        ),
                      },
                      {
                        cle: 'dateEeq', libelle: 'Reçu le', type: 'date', valeur: (c) => c.dateEeq || '',
                        style: { color: 'var(--text-muted)' },
                        rendu: (c) => (
                          <>
                            {c.dateEeq ? fmtDate(c.dateEeq) : '—'}
                            {c.precisionDate === 'estimee' && (
                              <span title="Le registre ne porte que le mois : la date est estimée." style={{ cursor: 'help' }}> ~</span>
                            )}
                          </>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Saisie d'une mesure : l'état du CQI est consulté AVANT d'enregistrer, et
 *  affiché tel quel. Le serveur le relit et tranche — l'écran n'anticipe que
 *  pour éviter une saisie inutile. */
function AjoutMesure({ detail, onFait }: { detail: Detail; onFait: () => void }) {
  const [f, setF] = useState({ analyte: '', equipement: '', valeur: '', unite: '', lotReactif: '', methode: '', remarque: '' });
  const [etat, setEtat] = useState<EtatCqi | null>(null);
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const { session, settings } = useGed();
  // Non dérogeable sauf pour qui porte déjà ce droit — ou un responsable qui
  // s'authentifie pour ce seul geste depuis le poste du technicien
  // (06/09/2026, `autoriserResponsable` côté serveur).
  const peutDeroger = estBiologiste(session.profile, settings?.perms);
  const authDerogation = useAutorisationResponsable();

  const jour = detail.envoi.datePassage || '';
  const choix = useMemo(
    () => detail.proposables.filter((p) => f.analyte === '' || p.analyte === f.analyte),
    [detail.proposables, f.analyte],
  );

  // Interrogation de l'état du CQI dès qu'analyte, automate et date sont connus.
  useEffect(() => {
    let vivant = true;
    if (!f.analyte || !jour) {
      // Rien à interroger : l'état est effacé par le formulaire lui-même, pas
      // ici — remettre `null` en tête d'effet déclencherait un rendu en cascade.
      return () => { vivant = false; };
    }
    const q = new URLSearchParams({ analyte: f.analyte, date: jour });
    if (f.equipement) q.set('equipement', f.equipement);
    void fetch(`/api/ged/ged/cqe/etat-cqi?${q}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setEtat(j); })
      .catch(() => { if (vivant) setEtat(null); });
    return () => { vivant = false; };
  }, [f.analyte, f.equipement, jour]);

  const derogationRequise = etat !== null && etat.conforme !== true;
  const bloque = !f.analyte || !f.valeur.trim() || !jour
    || (derogationRequise && (motif.trim().length < MOTIF_MIN || (!peutDeroger && !authDerogation.pret)));

  const enregistrer = async () => {
    const r = await fetchEcriture('/api/ged/ged/cqe/mesures', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        envoi: detail.envoi.envoiId, analyte: f.analyte, equipementCode: f.equipement || null,
        valeur: f.valeur, unite: f.unite || null, lotReactif: f.lotReactif || null,
        methode: f.methode || null, remarque: f.remarque || null,
        date: jour, derogationMotif: motif.trim() || null,
        // Un biologiste déjà habilité agit sans ce champ (revérifié sur son
        // propre profil côté serveur) ; sinon c'est lui qui l'authentifie.
        responsable: derogationRequise ? authDerogation.payload : undefined,
      }),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setErreur(d?.error?.message || 'Enregistrement refusé.');
      return;
    }
    setErreur('');
    setF({ analyte: '', equipement: '', valeur: '', unite: '', lotReactif: '', methode: '', remarque: '' });
    setMotif(''); authDerogation.reinitialiser();
    setEtat(null);
    onFait();
  };

  if (!jour) {
    return (
      <div style={{ padding: '10px 12px', background: 'var(--amber-50)', border: '1px solid var(--amber-300)', borderRadius: 'var(--radius-md)', fontSize: 12.5, color: 'var(--text-body)' }}>
        Renseignez la <strong>date de passage</strong> de l’envoi avant de saisir des mesures :
        c’est elle qui détermine quel contrôle interne fait foi.
      </div>
    );
  }

  return (
    <div style={{ padding: 14, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
        <div><label style={lbl} htmlFor="m-an">Paramètre *</label>
          <Selecteur id="m-an" value={f.analyte}
            onChange={(v) => {
              const p = detail.proposables.find((x) => x.analyte === v);
              setF({ ...f, analyte: v, equipement: p?.equipement || '', unite: p?.unite || f.unite });
              // L'état du CQI affiché est celui de l'analyte PRÉCÉDENT tant que
              // la nouvelle réponse n'est pas là : l'effacer ici évite qu'un
              // « conforme » périmé serve de feu vert à une autre mesure.
              setEtat(null);
              setMotif('');
            }}
            style={inputStyle}>
            <option value="">— choisir —</option>
            {[...new Map(detail.proposables.map((p) => [p.analyte, p])).values()].map((p) => (
              <option key={p.analyte} value={p.analyte}>{p.analyteNom}</option>
            ))}
          </Selecteur></div>
        <div><label style={lbl} htmlFor="m-eq">Automate</label>
          <Selecteur id="m-eq" value={f.equipement} onChange={(v) => setF({ ...f, equipement: v })} style={inputStyle}>
            <option value="">— tous —</option>
            {choix.map((p) => <option key={p.equipement} value={p.equipement}>{p.equipement} · {p.equipementNom}</option>)}
          </Selecteur></div>
        <div><label style={lbl} htmlFor="m-val">Valeur transmise *</label>
          <input id="m-val" value={f.valeur} onChange={(e) => setF({ ...f, valeur: e.target.value })}
            inputMode="decimal" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>
        <div><label style={lbl} htmlFor="m-u">Unité</label>
          <input id="m-u" value={f.unite} onChange={(e) => setF({ ...f, unite: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="m-lot">Lot de réactif</label>
          <input id="m-lot" value={f.lotReactif} onChange={(e) => setF({ ...f, lotReactif: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="m-me">Méthode</label>
          <input id="m-me" value={f.methode} onChange={(e) => setF({ ...f, methode: e.target.value })} placeholder="ECLIA…" style={inputStyle} /></div>
      </div>

      {/* État du contrôle interne — la condition de transmission. */}
      {etat && (
        <div style={{
          padding: '10px 12px', borderRadius: 'var(--radius-md)',
          background: etat.conforme === true ? 'var(--green-50)' : 'var(--red-50)',
          border: `1px solid ${etat.conforme === true ? 'var(--green-500)' : 'var(--red-200)'}`,
          display: 'grid', gap: 5,
        }}>
          <strong style={{ fontSize: 12.5, color: etat.conforme === true ? 'var(--green-600)' : 'var(--red-600)' }}>
            {etat.conforme === true
              ? `Contrôle interne conforme — passage du ${fmtDate(etat.datePassage || '')}`
              : etat.conforme === false
                ? `Contrôle interne EN REJET — passage du ${fmtDate(etat.datePassage || '')}`
                : 'Aucun contrôle interne trouvé pour ce paramètre à cette date'}
          </strong>
          {/* Une absence de CQI n'est pas une conformité : c'est une absence de preuve. */}
          {etat.conforme === null && (
            <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
              {etat.motif}. Transmettre sans contrôle interne exige une dérogation.
            </span>
          )}
          {typeof etat.joursEcart === 'number' && etat.joursEcart > ECART_CQI_ALERTE && (
            <span style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}>
              Ce contrôle date de {etat.joursEcart} jours avant le passage — il n’atteste pas des
              conditions du jour.
            </span>
          )}
        </div>
      )}

      {derogationRequise && (
        <div style={{ display: 'grid', gap: 5 }}>
          <label style={lbl} htmlFor="m-mo">Motif de dérogation * (≥ {MOTIF_MIN} caractères)</label>
          <textarea id="m-mo" value={motif} onChange={(e) => setMotif(e.target.value)} rows={2}
            placeholder="Décision du biologiste : pourquoi transmettre malgré ce contrôle interne ?"
            style={{ ...inputStyle, height: 'auto', padding: '8px 10px', resize: 'vertical' }} />
          {peutDeroger ? (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              La dérogation sera enregistrée à votre nom, avec sa date.
            </span>
          ) : (
            <ChampsResponsable {...authDerogation} libelle="cette dérogation" />
          )}
        </div>
      )}

      <FieldError>{erreur}</FieldError>
      <div>
        <Bouton variante="primaire" taille="sm" onClick={() => void run('m', enregistrer)} disabled={bloque || estOccupe()}
          chargement={estOccupe('m')} libelleChargement="Enregistrement…"
          title={bloque ? 'Paramètre, valeur et — si dérogation — motif sont requis' : undefined}>
          Ajouter la mesure
        </Bouton>
      </div>
    </div>
  );
}

/** Panneau d'un envoi ouvert : check-list, date de passage, mesures. */
function PanneauEnvoi({ envoiId, envoiRef, onChange }: { envoiId: string; envoiRef?: string | null; onChange: () => void }) {
  const { session, settings } = useGed();
  const peutSaisir = peut(settings?.perms, session.profile, 'cqeSaisir');
  const [d, setD] = useState<Detail | null>(null);
  const [message, setMessage] = useState('');
  const { run, estOccupe } = useBusy();
  const { demander, confirmation } = useConfirmation();

  const charger = useCallback(async () => {
    const r = await fetch(`/api/ged/ged/cqe/envois/${envoiId}`, { credentials: 'include' });
    setD(r.ok ? await r.json() : null);
  }, [envoiId]);
  // `.then()` et non `await` : une fonction `async` appelée directement dans
  // l'effet masque le fait que les `setState` suivent le premier await.
  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/cqe/envois/${envoiId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); });
    return () => { vivant = false; };
  }, [envoiId]);

  const majEnvoi = async (corps: Record<string, unknown>) => {
    const r = await fetchEcriture(`/api/ged/ged/cqe/envois/${envoiId}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corps),
    });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setMessage(j?.error?.message || 'Enregistrement refusé.');
      return false;
    }
    setMessage('');
    await charger();
    onChange();
    return true;
  };

  if (!d) return <div style={{ padding: 16, fontSize: 12.5, color: 'var(--text-muted)' }}>Chargement…</div>;

  const gabarit = d.programme?.checkList || [];
  const coches = d.envoi.checkList || [];
  const tousCoches = gabarit.length > 0 && gabarit.every((_, i) => coches[i]);

  return (
    <div style={{ padding: '0 14px 16px', display: 'grid', gap: 14 }}>
      {/* Dans le PANNEAU et non sur la ligne : la ligne est un <button>
          (dépliage), un bouton dans un bouton est invalide. Règle RQ du 18/08,
          câblée le 24/08/2026 — les saisies de mesures s'écrivaient au journal
          de l'envoi sans accès à l'écran. */}
      {envoiRef && <div><BoutonJournal reference={envoiRef} quoi="de cet envoi" /></div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={lbl} htmlFor={`dp-${envoiId}`}>Date de passage</label>
          <input id={`dp-${envoiId}`} type="date" max={aujourdhui()} value={d.envoi.datePassage || ''}
            disabled={!peutSaisir}
            onChange={(e) => void majEnvoi({ datePassage: e.target.value, statut: e.target.value ? 'passe' : 'planifie' })}
            style={{ ...inputStyle, minWidth: 150 }} />
        </div>
        <div>
          <label style={lbl} htmlFor={`ne-${envoiId}`}>N° d’échantillon</label>
          <input id={`ne-${envoiId}`} defaultValue={d.envoi.numeroEchantillon || ''} disabled={!peutSaisir}
            onBlur={(e) => void majEnvoi({ numeroEchantillon: e.target.value })}
            style={{ ...inputStyle, minWidth: 120 }} />
        </div>
        {peutSaisir && d.envoi.statut !== 'envoye' && (
          /* « Déclarer transmis » est l'action principale du panneau ; « raté » l'alternative, avant elle. */
          <PiedActions
            taille="sm"
            secondaires={d.envoi.statut === 'planifie'
              ? [{ libelle: 'Déclarer raté', title: 'L’échantillon n’a pas été passé dans les délais', onClick: () => void majEnvoi({ statut: 'rate' }) }]
              : []}
            principale={{
              libelle: 'Déclarer transmis', disabled: estOccupe() || !d.mesures.length,
              title: !d.mesures.length ? 'Aucune mesure saisie' : tousCoches ? undefined : 'La check-list n’est pas complète',
              onClick: () => void run('env', () => majEnvoi({ statut: 'envoye', dateEnvoi: aujourdhui() })),
            }}
          />
        )}
      </div>

      {/* Check-list : cochée pour CET envoi, pas recopiée d'un gabarit. */}
      {gabarit.length > 0 && (
        <div>
          <div style={lbl}>
            Conditions de passage — {gabarit.filter((_, i) => coches[i]).length} / {gabarit.length}
          </div>
          <div style={{ display: 'grid', gap: 3 }}>
            {gabarit.map((item, i) => (
              <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--text-body)' }}>
                <input type="checkbox" checked={!!coches[i]} disabled={!peutSaisir}
                  onChange={(e) => {
                    const suite = gabarit.map((_, k) => (k === i ? e.target.checked : !!coches[k]));
                    void majEnvoi({ checkList: suite });
                  }}
                  style={{ marginTop: 2, accentColor: 'var(--color-primary)' }} />
                <span>{item}</span>
              </label>
            ))}
          </div>
          {!tousCoches && (
            <div style={{ fontSize: 12.5, color: 'var(--amber-600)', marginTop: 5 }}>
              Certaines conditions ne sont pas cochées. Deux d’entre elles supposent une preuve
              ailleurs : la pipette calibrée relève du raccordement métrologique, la maintenance
              LFPC de la fiche de vie de l’automate.
            </div>
          )}
        </div>
      )}

      {/* Mesures transmises */}
      <div>
        <div style={lbl}>Mesures transmises — {d.mesures.length}</div>
        {d.mesures.length === 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '8px 0' }}>Aucune mesure saisie.</div>
        )}
        <div style={{ display: 'grid', gap: 5 }}>
          {d.mesures.map((m) => (
            <div key={m.mesureId} style={{
              display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
              padding: '7px 10px', borderRadius: 'var(--radius-md)',
              // Un CQI inconnu n'est PAS un rejet : le peindre en rouge comme un
              // rejet reviendrait à accuser sur une absence de preuve.
              background: m.cqiConforme === true ? 'var(--surface-sunken)'
                : m.cqiConforme === false ? 'var(--red-50)' : 'var(--amber-50)',
            }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', minWidth: 150 }}>{m.analyteLibelle}</span>
              <span style={{ fontSize: 12.5, fontFamily: 'var(--font-mono)' }}>{m.valeur}{m.unite ? ` ${m.unite}` : ''}</span>
              {m.equipementCode && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{m.equipementCode}</span>}
              <span title={m.cqiConforme === null
                ? 'Aucun passage de contrôle interne trouvé pour ce paramètre à cette date ou avant. Ce n’est ni conforme ni non conforme : on ne sait pas.'
                : `Dernier passage CQI${m.cqiPassage ? ` du ${fmtDate(m.cqiPassage)}` : ''}${m.cqiStatut ? ` — ${m.cqiStatut}` : ''}`}
                style={{
                  fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', cursor: 'help',
                  background: m.cqiConforme === true ? 'var(--green-50)'
                    : m.cqiConforme === false ? 'var(--red-100)' : 'var(--amber-100)',
                  color: m.cqiConforme === true ? 'var(--green-600)'
                    : m.cqiConforme === false ? 'var(--red-600)' : 'var(--text-strong)',
                }}>
                {m.cqiConforme === true ? 'CQI conforme'
                  : m.cqiConforme === false ? 'CQI en rejet' : 'CQI inconnu'}
                {m.cqiPassage ? ` · ${fmtDate(m.cqiPassage)}` : ''}
              </span>
              {/* Ce que la feuille DISAIT, quand elle contredit ce qui est
                  démontrable. C'est le cœur du lot : la colonne « CQI CONFORME ? »
                  se cochait à la main, sans que rien ne la vérifie. */}
              {m.cqiDeclareFeuille === 'oui' && m.cqiConforme === false && (
                <span title="La feuille de saisie portait « CQI CONFORME = OUI » ; le dernier contrôle interne de ce paramètre était en rejet."
                  style={{
                    fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px',
                    background: 'var(--red-600)', color: 'var(--text-on-primary)', cursor: 'help',
                  }}>
                  feuille : « conforme »
                </span>
              )}
              {m.cqiDeclareFeuille === 'non' && (
                <span title="La feuille de saisie portait « CQI CONFORME = NON » et la valeur a tout de même été transmise, sans dérogation tracée."
                  style={{
                    fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px',
                    background: 'var(--red-100)', color: 'var(--red-600)', cursor: 'help',
                  }}>
                  transmis malgré « CQI = NON »
                </span>
              )}
              {/* C6 — ce qui est revenu de l'organisme. Le z-score est la SEULE
                  mesure de justesse dont dispose le laboratoire pour un analyte
                  qu'aucun contrôle interne ne borne. */}
              {m.retour && (
                <span title={`${m.retour.programme} · résultat ${m.retour.resultat}`
                  + `${m.retour.precisionDate === 'estimee' ? ' · période estimée : le registre ne porte que le mois' : ''}`
                  + `${m.retour.actionRef ? ` · action ${m.retour.actionRef}` : ''}`}
                  style={{
                    fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', cursor: 'help',
                    background: m.retour.resultat === 'satisfaisant' ? 'var(--green-50)' : 'var(--red-100)',
                    color: m.retour.resultat === 'satisfaisant' ? 'var(--green-600)' : 'var(--red-600)',
                  }}>
                  z = {m.retour.zscore === null ? '—' : m.retour.zscore}
                  {m.retour.precisionDate === 'estimee' ? ' ·  ~' : ''}
                </span>
              )}
              {m.derogationPar && (
                <span title={m.derogationMotif || ''} style={{
                  fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px',
                  background: 'var(--violet-50)', color: 'var(--violet-600)', cursor: 'help',
                }}>
                  dérogation · {m.derogationPar}
                </span>
              )}
              <span style={{ flex: 1 }} />
              {peutSaisir && (
                /* Suppression IRRÉVERSIBLE d'une mesure : danger + confirmation (elle n'en avait aucune). */
                <Bouton variante="danger" taille="sm" disabled={estOccupe()}
                  onClick={async () => {
                    if (!(await demander(`Supprimer cette mesure ?\n\n${m.analyteLibelle || m.analyte || 'Mesure'}${m.valeur !== null ? ` · ${m.valeur}${m.unite ? ` ${m.unite}` : ''}` : ''}\n\nElle ne se récupère pas.`, { titre: 'Supprimer la mesure', confirmer: 'Supprimer', danger: true }))) return;
                    void run(m.mesureId, async () => {
                      const r = await fetchEcriture(`/api/ged/ged/cqe/mesures/${m.mesureId}`, { method: 'DELETE', credentials: 'include' });
                      if (r.ok) { await charger(); onChange(); }
                    });
                  }}>Supprimer</Bouton>
              )}
            </div>
          ))}
        </div>
      </div>

      {d.rapprochement && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '84ch' }}>
          <strong>{d.rapprochement.apparies}</strong> mesure(s) rapprochée(s) d’un z-score reçu,
          {' '}<strong>{d.rapprochement.mesuresSansRetour}</strong> sans retour rapproché. Le rapprochement
          se reconstruit sur la discipline, le mois et l’analyte — les deux registres n’ont aucune
          clé commune. Une mesure sans z-score n’est donc pas forcément une mesure sans réponse
          de l’organisme.
        </p>
      )}

      {peutSaisir && d.envoi.statut !== 'envoye' && <AjoutMesure detail={d} onFait={() => { void charger(); onChange(); }} />}
      {message && <FieldError>{message}</FieldError>}
      {confirmation}
    </div>
  );
}

export function CqeEnvois() {
  const { session, settings, showToast } = useGed();
  const peutSaisir = peut(settings?.perms, session.profile, 'cqeSaisir');
  const isRQ = session.roles.includes('rq');
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [nouveau, setNouveau] = useState<string | null>(null);
  const [editionProg, setEditionProg] = useState<Programme | 'nouveau' | null>(null);
  const [voirArchives, setVoirArchives] = useState(false);
  const [f, setF] = useState({ periodeLibelle: '', dateLimite: '', numeroEchantillon: '' });
  const { run, estOccupe } = useBusy();
  const { demander, confirmation } = useConfirmation();

  const charger = useCallback(async () => {
    const r = await fetch('/api/ged/ged/cqe/envois', { credentials: 'include' });
    if (r.ok) setProgrammes((await r.json()).programmes || []);
  }, []);

  /* ── SUPPRIMER, LE PENDANT DE CRÉER (RQ, 26/08/2026) ───────────────────────
   * « Donner la main au RQ de supprimer des EEQ et CQE au besoin, comme il a la
   * main pour en créer. » Sans cela, une reprise fausse ou un doublon d'import
   * ne se retire que par la base — c'est-à-dire sans aucune trace.
   *
   * ARCHIVER reste le geste normal d'un registre qui fait preuve : la ligne
   * sort du courant, reste citable par une NC ou un audit, et revient. La
   * suppression est là pour ce qui n'aurait jamais dû exister.
   *
   * La confirmation NOMME ce qui part, et compte les mesures emportées : « cet
   * envoi » et « cet envoi et ses trente-cinq résultats transmis » n'engagent
   * pas la même décision. Le serveur écrit au journal ce que la fiche disait —
   * le retrait reste opposable après que la fiche a disparu. */
  const supprimerEnvoi = async (p: Programme, e: Programme['envois'][number]) => {
    const quoi = `${p.libelle} · ${e.periodeLibelle || e.ref || 'envoi sans période'}`;
    const combien = e.mesures ? `\n\n${e.mesures} mesure(s) seront supprimées avec lui.` : '';
    if (!(await demander(`Supprimer définitivement cet envoi ?\n\n${quoi}${combien}\n\nLe retrait est écrit au journal ; l’envoi, lui, ne se récupère pas.`,
      { titre: 'Supprimer l’envoi', confirmer: 'Supprimer', danger: true }))) return;
    const r = await fetchEcriture(`/api/ged/ged/cqe/envois/${e.envoiId}`, { method: 'DELETE', credentials: 'include' });
    if (!r.ok) { showToast((await r.json().catch(() => null))?.error?.message || 'Suppression refusée.'); return; }
    showToast('Envoi supprimé — le retrait est au journal.');
    await charger();
  };

  const supprimerProgramme = async (p: Programme) => {
    /* La cascade est assumée (RQ, 26/08/2026), mais elle est COMPTÉE avant
       d'être proposée : « ce programme » et « ce programme, ses 46 envois et
       ses 435 mesures » n'engagent pas la même décision. Les mesures sont
       additionnées depuis les envois déjà chargés — le serveur les recompte de
       son côté, l'écran ne fait qu'annoncer ce qui va partir. */
    const mesures = p.envois.reduce((n, e) => n + (e.mesures || 0), 0);
    const emporte = p.envois.length
      ? `\n\n${p.envois.length} envoi(s) et ${mesures} mesure(s) seront supprimés avec lui.`
      : '';
    if (!(await demander(`Supprimer définitivement ce programme ?\n\n${p.libelle} · ${p.code}${emporte}\n\nLe retrait est écrit au journal ; le programme, lui, ne se récupère pas.`,
      { titre: 'Supprimer le programme', confirmer: 'Supprimer', danger: true }))) return;
    const r = await fetchEcriture(`/api/ged/ged/cqe/programmes/${p.programmeId}`, { method: 'DELETE', credentials: 'include' });
    if (!r.ok) { showToast((await r.json().catch(() => null))?.error?.message || 'Suppression refusée.'); return; }
    showToast('Programme supprimé — le retrait est au journal.');
    await charger();
  };
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/cqe/envois', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { programmes: [] }))
      .then((j) => { if (vivant) setProgrammes(j.programmes || []); })
      .catch(() => { if (vivant) setProgrammes([]); });
    return () => { vivant = false; };
  }, []);

  const enRetard = programmes.flatMap((p) => p.envois.filter((e) => e.enRetard));
  const horsDelai = programmes.flatMap((p) => p.envois.filter((e) => e.passeEnRetard));
  const derogations = programmes.reduce((a, p) => a + p.envois.reduce((b, e) => b + e.derogations, 0), 0);
  // Une déclaration contredite n'est pas une dérogation : personne n'a rien
  // décidé, la case a simplement été cochée « conforme » sans que ce le soit.
  const contredites = programmes.reduce((a, p) => a + p.envois.reduce((b, e) => b + e.declarationContredite, 0), 0);

  const creer = async (programmeId: string) => {
    const r = await fetchEcriture('/api/ged/ged/cqe/envois', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ programme: programmeId, ...f }),
    });
    if (!r.ok) return;
    const j = await r.json();
    setNouveau(null);
    setF({ periodeLibelle: '', dateLimite: '', numeroEchantillon: '' });
    await charger();
    setOuvert(j.envoiId);
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <H1 eyebrow={<>Évaluation externe · <RefISO code="7.3.7.3" prefixe="" /></>} title="EEQ — Envois" />
          {/* Les saisies d'un envoi SANS référence propre chaînent sous
              « REG-CQE » (24/08/2026) : leur journal se lit ici. */}
          <BoutonJournal reference="REG-CQE" quoi="des envois sans référence" />
        </div>
        {isRQ && (
          <Bouton variante="primaire" onClick={() => setEditionProg('nouveau')}>
            <I n="plus" s={14} />Ajouter un programme
          </Bouton>
        )}
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '82ch' }}>
        Ce que le laboratoire <strong>transmet</strong> à l’organisme. Un contrôle externe s’exécute
        dans les conditions de routine, contrôle interne validé compris : l’état du CQI est relu en
        base à la date du passage, et transmettre malgré un rejet exige une dérogation du biologiste.
      </p>

      {contredites > 0 && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid var(--red-200)' }}>
          <I n="alert" s={16} />
          <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--red-600)' }}>
              {contredites} mesure{contredites > 1 ? 's' : ''} transmise{contredites > 1 ? 's' : ''} avec une déclaration « CQI conforme » contredite par la carte de contrôle.
            </strong>{' '}
            Reprises des feuilles de saisie : la case était cochée à la main, le dernier contrôle
            interne de l’analyte était en rejet. Aucune dérogation n’a été posée à leur place —
            une dérogation est nominative et datée, elle ne se reconstitue pas après coup.
          </div>
        </div>
      )}

      {(enRetard.length > 0 || horsDelai.length > 0 || derogations > 0) && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: enRetard.length ? 'var(--red-50)' : 'var(--amber-50)', border: `1px solid ${enRetard.length ? 'var(--red-200)' : 'var(--amber-300)'}` }}>
          <I n="alert" s={16} />
          <strong style={{ fontSize: 13, color: 'var(--text-strong)' }}>
            {enRetard.length} envoi(s) encore à faire au-delà de leur date limite
            · {horsDelai.length} passé(s) hors délai
            · {derogations} mesure(s) transmise(s) par dérogation
          </strong>
        </div>
      )}

      {/* GED-15 c — le bilan GLOBAL, replié. Le panneau d'un envoi ne peut dire
          que « n mesures sans retour » ; seul ce bilan-ci sait dire l'inverse,
          les z-scores reçus qui ne se rattachent à aucun envoi tracé. */}
      <BilanRapprochement />

      {programmes.length === 0 && (
        <div style={{ ...card, padding: 26, textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
          Aucun programme d’évaluation externe.
        </div>
      )}

      {/* Le bascule reste, mais pour une raison neuve : plus rien ne s'archive
          ici, et les lignes archivées AVANT le 26/08/2026 doivent rester
          atteignables — sinon elles disparaîtraient de l'écran sans jamais
          pouvoir être supprimées. */}
      {programmes.some((p) => p.archive) && (
        <Bouton taille="sm" onClick={() => setVoirArchives((v) => !v)} style={{ width: 'fit-content' }} aria-pressed={voirArchives}>
          <I n="archive" s={13} />
          {voirArchives ? 'Masquer' : 'Afficher'} les programmes archivés ({programmes.filter((p) => p.archive).length})
        </Bouton>
      )}

      {programmes.filter((p) => voirArchives || !p.archive).map((p) => (
        <div key={p.programmeId} style={{ ...card, padding: 0, overflow: 'hidden', opacity: p.archive ? 0.75 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '11px 14px', borderBottom: '1px solid var(--border-default)' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>{p.libelle}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
              {p.code}{p.reference ? ` · ${p.reference}` : ''}{p.cycle ? ` · cycle ${p.cycle}` : ''}
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.organisme} · {p.periodicite}</span>
            {p.archive && <BadgeArchive motif={p.archiveMotif} />}
            <span style={{ flex: 1 }} />
            {/* Pas d'archivage ici : « je ne veux rien archiver, je veux
                supprimer » (RQ, 26/08/2026). Ce qu'on retire de ce registre est
                de la reprise fausse et de l'import d'essai — une ligne mise de
                côté au lieu d'être retirée continue de peser sur les comptes du
                rapprochement. */}
            {isRQ && (
              <ActionsLigne
                onCorriger={() => setEditionProg(p)}
                libelleSuppression="Supprimer définitivement le programme"
                onSupprimer={() => void supprimerProgramme(p)} />
            )}
            {peutSaisir && !p.archive && (
              <Bouton taille="sm" onClick={() => setNouveau(nouveau === p.programmeId ? null : p.programmeId)}>
                <I n="plus" s={13} />Nouvel envoi
              </Bouton>
            )}
          </div>

          {nouveau === p.programmeId && (
            <div style={{ padding: 14, background: 'var(--surface-sunken)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div><label style={lbl} htmlFor={`pl-${p.programmeId}`}>Période</label>
                <input id={`pl-${p.programmeId}`} value={f.periodeLibelle} placeholder="01-2026"
                  onChange={(e) => setF({ ...f, periodeLibelle: e.target.value })} style={{ ...inputStyle, minWidth: 120 }} /></div>
              <div><label style={lbl} htmlFor={`dl-${p.programmeId}`}>Date limite</label>
                <input id={`dl-${p.programmeId}`} type="date" value={f.dateLimite}
                  onChange={(e) => setF({ ...f, dateLimite: e.target.value })} style={{ ...inputStyle, minWidth: 150 }} /></div>
              <div><label style={lbl} htmlFor={`ne2-${p.programmeId}`}>N° échantillon</label>
                <input id={`ne2-${p.programmeId}`} value={f.numeroEchantillon}
                  onChange={(e) => setF({ ...f, numeroEchantillon: e.target.value })} style={{ ...inputStyle, minWidth: 110 }} /></div>
              <PiedActions
                taille="sm"
                annuler={{ onClick: () => setNouveau(null) }}
                principale={{ libelle: 'Créer', libelleChargement: 'Création…', chargement: estOccupe('c'), disabled: estOccupe(), onClick: () => void run('c', () => creer(p.programmeId)) }}
              />
            </div>
          )}

          {p.envois.length === 0 && (
            <div style={{ padding: '14px', fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun envoi enregistré.</div>
          )}

          {p.envois.map((e) => {
            const st = STATUTS[e.statut] || STATUTS.planifie;
            const actif = ouvert === e.envoiId;
            return (
              <div key={e.envoiId} style={{ borderTop: '1px solid var(--border-default)' }}>
                {/* ⚠️ Le bouton de suppression est FRÈRE du bouton de dépliage,
                    jamais dedans : un `<button>` imbriqué dans un `<button>`
                    est invalide, et le navigateur déclenche alors le mauvais
                    des deux — on croirait ouvrir l'envoi et on le supprimerait. */}
                <div style={{ display: 'flex', alignItems: 'stretch', background: e.enRetard ? 'var(--red-50)' : 'none' }}>
                <button onClick={() => setOuvert(actif ? null : e.envoiId)} aria-expanded={actif}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 14px', background: e.enRetard ? 'var(--red-50)' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', minWidth: 90 }}>
                    {e.periodeLibelle || e.ref || '—'}
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: st.bg, color: st.fg }}>{st.label}</span>
                  {e.dateLimite && (
                    <span style={{ fontSize: 12.5, color: e.enRetard || e.passeEnRetard ? 'var(--red-600)' : 'var(--text-muted)', fontWeight: e.enRetard || e.passeEnRetard ? 700 : 400 }}>
                      limite {fmtDate(e.dateLimite)}
                      {e.enRetard ? ' — dépassée' : e.passeEnRetard ? ' — passé hors délai' : ''}
                    </span>
                  )}
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {e.mesures} mesure(s) · check-list {e.checkListCoches}/{e.checkListTotal}
                  </span>
                  {e.declarationContredite > 0 && (
                    <span title="La feuille portait « CQI CONFORME = OUI » alors que le dernier contrôle interne était en rejet."
                      style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--red-600)', color: 'var(--text-on-primary)', cursor: 'help' }}>
                      {e.declarationContredite} déclaration(s) contredite(s)
                    </span>
                  )}
                  {e.cqiInconnu > 0 && (
                    <span title="Aucun passage de contrôle interne n’est enregistré pour ces paramètres à la date du passage. Ce n’est pas un rejet : les cartes CQI reprises commencent fin 2025."
                      style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--amber-100)', color: 'var(--text-strong)', cursor: 'help' }}>
                      {e.cqiInconnu} CQI inconnu(s)
                    </span>
                  )}
                  {e.derogations > 0 && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--violet-50)', color: 'var(--violet-600)' }}>
                      {e.derogations} dérogation(s)
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  <I n={actif ? 'check' : 'diff'} s={13} />
                </button>
                {isRQ && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', paddingInlineEnd: 10 }}>
                    <Bouton variante="danger" taille="sm" onClick={() => void supprimerEnvoi(p, e)}
                      title="Supprimer définitivement cet envoi et ses mesures"
                      aria-label={`Supprimer l’envoi ${e.periodeLibelle || e.ref || ''}`}>
                      <I n="trash" s={13} />
                    </Bouton>
                  </span>
                )}
                </div>
                {actif && <PanneauEnvoi envoiId={e.envoiId} envoiRef={e.ref} onChange={() => void charger()} />}
              </div>
            );
          })}
        </div>
      ))}

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '82ch' }}>
        Les z-scores reçus en retour se consultent dans « EEQ — Résultats reçus ». Cet écran
        décrit ce qui est envoyé, et à quelles conditions.
      </p>

      {editionProg !== null && (
        <FormulaireLigne chemin="ged/cqe/programmes" titreCreation="Ajouter un programme EEQ"
          champs={PROGRAMME_CHAMPS}
          ligne={editionProg === 'nouveau' ? null : { ...editionProg, documentId: editionProg.programmeId }}
          onClose={() => setEditionProg(null)} onDone={() => void charger()} />
      )}
      {confirmation}
    </div>
  );
}
